// InventoryManagement.tsx
import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  AlertTriangle,
  Building2,
  Plus,
  PackagePlus,
  PlusCircle,
  CircleChevronLeft,
} from "lucide-react";
import Select from "react-select";
import InventoryList from "./InventoryList";
import WholesalerList from "./WholesalerList";
import InvoiceHistory from "./InvoiceHistory";
import AddNewWholesalerModal from "../../../../shared/AddNewWholesalerModal";
import ExpiryAlert from "./ExpiryAlert";
import { AddNewProductModal } from "../../../../shared/addnewproduct/AddNewProductModal";
import InwardNewstockModal from "../../../../shared/InwardNewstockModal";
import ReturnItemEntryModal from "../../../../shared/ReturnItemEntryModal";
import ManufacturerModal from "../../../../shared/AddNewManufacturer";
import MarketerModal from "../../../../shared/AddNewMarketer";
import ManufacturerList from "./ManufacturerList";
import MarketerList from "./MarketerList";
import RequestedStock from "../../Clinic/RequestedStock";
// import defaultAvatar from "../../../../assets/images/default_avatar.jpg";

type TabType = "inventory" | "wholesaler" | "manufacturer" | "invoice"|"marketer"|"requestedStock";

interface Tab {
  id: TabType;
  label: string;
  component: React.ComponentType<any>;
}

interface ClinicOption {
  label: string;
  value: string;
}

const InventoryManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("inventory");
  const [searchTerm, setSearchTerm] = useState("");
  const [showWholesalerModal, setShowWholesalerModal] = useState(false);
  const [showManufacturerModal, setShowManufacturerModal] = useState(false);
  const [showMarketerModal, setShowMarketerModal] = useState(false);
  const [showexpiryAlert, setShowexpiryAlert] = useState(false);
  const [showAddNewProductModal, setShowAddNewProductModal] = useState(false);
  const [showInwardNewStockModal, setShowInwardNewStockModal] = useState(false);
  const [showReturnItemEntryModal, setShowReturnItemEntryModal] =
    useState(false);
  const [selectedWholeSaler, setSelectedWholesaler] = useState(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedMarketer, setSelectedMarketer] = useState(null);
  const [clinicOptions, setClinicOptions] = useState<ClinicOption[]>([]);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [showSpModal,setShowSpModal]=useState(false);

  console.log("\n\n selectedWholeSaler", selectedWholeSaler, "\n\n");

  const tabs: Tab[] = [
    { id: "inventory", label: "All Inventory List", component: InventoryList },
    { id: "wholesaler", label: "Wholesaler List", component: WholesalerList },
    {
      id: "manufacturer",
      label: "Manufacturer List",
      component: ManufacturerList,
    },
    {
      id: "marketer",
      label: "Marketer List",
      component: MarketerList,
    },
    { id: "invoice", label: "Invoice History", component: InvoiceHistory },
    { id: "requestedStock", label: "Requested Stock", component: RequestedStock },
  ];

  // Get the active component based on current tab
  const getActiveComponent = () => {
    const activeTabData = tabs.find((tab) => tab.id === activeTab);
    return activeTabData?.component || InventoryList;
  };

  const ActiveComponent = getActiveComponent();

  useEffect(() => {
    // Example: fetch or get dynamic options
    const fetchClinics = async () => {
      // Replace this with real data fetching logic
      const data = [
        { id: "1", name: "Clinic A" },
        { id: "2", name: "Clinic B" },
      ];
      const formatted = data.map((clinic) => ({
        value: clinic.id,
        label: clinic.name,
      }));
      setClinicOptions(formatted);
    };

    fetchClinics();
  }, []);

  // Function to close all modals
  const closeAllModals = () => {
    setShowWholesalerModal(false);
    setShowManufacturerModal(false);
    setShowMarketerModal(false);
    setShowexpiryAlert(false);
    setShowAddNewProductModal(false);
    setShowInwardNewStockModal(false);
    setShowReturnItemEntryModal(false);
    setSelectedWholesaler(null);
    setSelectedManufacturer(null);
    setSelectedMarketer(null);
  };

  // Function to handle inward new stock button click
  const handleInwardNewStockClick = () => {
    closeAllModals();
    setShowInwardNewStockModal(true);
  };

  // Check if any modal is open
  const isAnyModalOpen =
    showWholesalerModal ||
    showexpiryAlert ||
    showAddNewProductModal ||
    showInwardNewStockModal ||
    showReturnItemEntryModal ||
    showManufacturerModal||
    showMarketerModal;

  // Render modal content
  const renderModalContent = () => {
    if (showWholesalerModal) {
      return (
        <AddNewWholesalerModal
          onClose={() => {
            setSelectedWholesaler(null);
            setShowWholesalerModal(false);
          }}
          selectedWholeSaler={selectedWholeSaler}
        />
      );
    }

    if (showexpiryAlert) {
      return <ExpiryAlert setShowexpiryAlert={setShowexpiryAlert} />;
    }

    if (showAddNewProductModal) {
      return (
        <AddNewProductModal
          onClose={() => {
            setShowAddNewProductModal(false);
          }}
          onSuccess={() => setShowAddNewProductModal(false)}
          productId={""}
          categoryId={0}
        />
      );
    }

    if (showInwardNewStockModal) {
      return (
        <InwardNewstockModal
          onClose={() => setShowInwardNewStockModal(false)}
        />
      );
    }

    if (showReturnItemEntryModal) {
      return (
        <ReturnItemEntryModal
          onClose={() => setShowReturnItemEntryModal(false)}
        />
      );
    }

    if (showManufacturerModal) {
      return (
        <ManufacturerModal
          onClose={() => {
            setShowManufacturerModal(false);
            setSelectedManufacturer(null);
          }}
          selectedWholeSaler={selectedManufacturer}
        />
      );
    }

    if (showMarketerModal) {
      return (
        <MarketerModal
          onClose={() => {
            setShowMarketerModal(false);
            setSelectedMarketer(null);
          }}
          selectedMarketer={selectedMarketer}
        />
      );
    }
    return null;
  };

  // Render main content
  const renderMainContent = () => (
    <div className="min-h-screen bg-gray-50 p-2">
      <div className="w-full">
        {/* Only show action buttons when showInvoiceDetails is false */}
        {( !showInvoiceDetails && !showSpModal ) && (
          <div className="flex justify-between items-center mb-4 font-semibold">
            {/* Left-aligned items */}
            <div className="flex gap-3">
              <button
                className="px-4 py-2 text-sm text-green-600 bg-white rounded-md hover:bg-green-50 border border-green-200 flex items-center gap-2 shadow-sm transition-colors"
                onClick={() => setShowManufacturerModal(true)}
              >
                <Plus size={16} />
                Add Manufacturer
              </button>
              <button
                className="px-4 py-2 text-sm text-green-600 bg-white rounded-md hover:bg-green-50 border border-green-200 flex items-center gap-2 shadow-sm transition-colors"
                onClick={() => setShowMarketerModal(true)}
              >
                <Plus size={16} />
                Add Marketer
              </button>
              <button
                className="px-4 py-2 text-sm text-green-600 bg-white rounded-md hover:bg-green-50 border border-green-200 flex items-center gap-2 shadow-sm transition-colors"
                onClick={() => setShowWholesalerModal(true)}
              >
                <Building2 size={16} />
                Add Wholesaler
              </button>
              <button
                className="px-4 py-2 text-sm text-amber-600 bg-white rounded-md hover:bg-amber-50 border border-amber-200 flex items-center gap-2 shadow-sm transition-colors"
                onClick={() => setShowexpiryAlert(true)}
              >
                <AlertTriangle size={16} />
                Expiry Alerts
              </button>
            </div>

            {/* Right-aligned items */}
            <div className="flex gap-3">
              <button
                className="px-4 py-2 text-sm text-blue-600 bg-white rounded-md hover:bg-blue-50 border border-blue-200 flex items-center gap-2 shadow-sm transition-colors"
                onClick={handleInwardNewStockClick}
              >
                <PackagePlus size={16} />
                Inward Stock
              </button>
              <button
                className="px-4 py-2 text-sm text-indigo-600 bg-white rounded-md hover:bg-indigo-50 border border-indigo-200 flex items-center gap-2 shadow-sm transition-colors"
                onClick={() => setShowAddNewProductModal(true)}
              >
                <PlusCircle size={16} />
                Add Product
              </button>
            </div>
          </div>
        )}

        {/* Tab Navigation - Only show when showInvoiceDetails is false */}
        {( !showInvoiceDetails && !showSpModal) && (
          <div className="bg-white border border-gray-200 p-2 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center">
              {/* Tabs */}
              <nav className="flex space-x-2  rounded-lg p-2 ">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
              px-5 py-2.5 rounded-md text-sm font-medium border border-gray-200 
              transition-all duration-200
              ${
                activeTab === tab.id
                  ? " text-green-700 bg-green-50 shadow-sm"
                  : "text-gray-600 hover:text-gray-800 hover:bg-white"
              }
            `}
                  >
                    <div className="flex items-center space-x-2">
                      <span>{tab.label}</span>
                    </div>
                  </button>
                ))}
              </nav>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-60 pl-10 pr-4 py-2.5 rounded-md bg-white border border-gray-200 text-sm focus:ring-2 focus:ring-green-400 outline-none"
                  />
                </div>

                {/* Filter */}
                {/* <button className="p-2 rounded-md bg-white border border-gray-200 hover:bg-gray-50">
          <Filter className="w-4 h-4 text-gray-500" />
        </button>  */}
              </div>
            </div>
          </div>
        )}

        {/* Active Tab Content - Show when no modal is open OR when showInvoiceDetails is true */}
        {(!isAnyModalOpen || showInvoiceDetails) && (
          <div className="transition-opacity duration-200">
            <ActiveComponent
              searchTerm={searchTerm}
              setShowWholesalerModal={() => setShowWholesalerModal(true)}
              setShowManufacturerModal={() => setShowManufacturerModal(true)}
              setShowMarketerModal={() => setShowMarketerModal(true)}
              setSelectedWholesaler={setSelectedWholesaler}
              setSelectedManufacturer={setSelectedManufacturer}
              setSelectedMarketer={setSelectedMarketer}
              setShowInvoiceDetails={setShowInvoiceDetails}
              showInvoiceDetails={showInvoiceDetails}
              setShowSpModal={setShowSpModal}
              showSpModal={showSpModal}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Show modals only when showInvoiceDetails is false */}
      {isAnyModalOpen && !showInvoiceDetails
        ? renderModalContent()
        : renderMainContent()}
    </>
  );
};

export default InventoryManagement;
